import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Button from '~/components/button';
import Container from '~/components/container';
import ContainerFlex from '~/components/containerFlex';
import Input, { MaskTypes } from '~/components/input';
import Loading from '~/components/loading';
import Pagination from '~/components/pagination';
import TableList from '~/components/tableList';
import { useAuth } from '~/hooks/contexts/useAuth';
import useApi, { ApiMethod } from '~/hooks/useApi';
import Endpoints from '~/services/endpoints';
import { FormField, setFormState } from '~/utils/form';
import { cpfMask } from '~/utils/masks';
import { IListColCampaing } from '~/views/Resource/Campaing/model';

import { formatDate, formatMoney } from './format';
import * as St from './listStyles';
import { PAGE_SIZE, readNegotiationPage } from './records';

export interface NegotiationRow {
  idNegociacao?: string;
  nome: string;
  cpf: string;
  data: string;
  valorOriginal?: number;
  desconto?: number;
  entrada?: number;
  parcelas?: number;
  vencimento: string;
  status: string;
  operador: string;
}

interface NegotiationListProps {
  status?: string;
  detailPath: string;
  emptyMessage: string;
  errorMessage: string;
  mapItem: (item: Record<string, unknown>) => NegotiationRow;
}

interface Form {
  cpf: FormField;
}

const INITIAL_FORM: Form = {
  cpf: { name: 'cpf', value: '' },
};

const listHeader = [
  { id: 1, value: 'Nome completo', maxChar: 24, textAlign: 'left' },
  { id: 2, value: 'CPF', maxChar: 16, textAlign: 'center' },
  { id: 3, value: 'Data', maxChar: 12, textAlign: 'center' },
  { id: 4, value: 'Valor original', maxChar: 16, textAlign: 'center' },
  { id: 5, value: 'Desconto', maxChar: 14, textAlign: 'center' },
  { id: 6, value: 'Entrada', maxChar: 14, textAlign: 'center' },
  { id: 7, value: 'Parcelas', maxChar: 10, textAlign: 'center' },
  { id: 8, value: 'Vencimento', maxChar: 12, textAlign: 'center' },
  { id: 9, value: 'Status', maxChar: 12, textAlign: 'center' },
  { id: 10, value: 'Operador', maxChar: 16, textAlign: 'center' },
  { id: 11, value: 'Ação', maxChar: 8, textAlign: 'center' },
];

const NegotiationList = ({
  status,
  detailPath,
  emptyMessage,
  errorMessage,
  mapItem,
}: NegotiationListProps): JSX.Element => {
  const history = useHistory();
  const { user } = useAuth();
  const [form, setForm] = useState<Form>(INITIAL_FORM);
  const [list, setList] = useState<IListColCampaing[][]>([]);
  const [feedback, setFeedback] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const [searchKey, setSearchKey] = useState(0);
  const [searchedCpf, setSearchedCpf] = useState('');
  const listNegotiations = useApi(
    Endpoints.debtNegotiation.listNegotiations,
    ApiMethod.POST,
  );

  const cpfDigits = form.cpf?.unmasked || '';
  const cpfIsPartial = cpfDigits.length > 0 && cpfDigits.length !== 11;

  const goToDetail = (_id: unknown, item: NegotiationRow) => {
    history.push(detailPath, item);
  };

  const buildRow = (
    row: NegotiationRow,
    group: {
      rowSpan?: number;
      hiddenIdentity?: boolean;
      showBottomBorder?: boolean;
    },
  ): IListColCampaing[] => [
    {
      border: true,
      title: 'Nome completo',
      value: row.nome || '-',
      maxChar: 24,
      textAlign: 'left',
      rowSpan: group.hiddenIdentity ? undefined : group.rowSpan,
      hidden: group.hiddenIdentity,
    },
    {
      border: true,
      title: 'CPF',
      value: row.cpf ? cpfMask(row.cpf).masked : '-',
      maxChar: 16,
      textAlign: 'center',
      rowSpan: group.hiddenIdentity ? undefined : group.rowSpan,
      hidden: group.hiddenIdentity,
    },
    {
      title: 'Data',
      value: formatDate(row.data),
      maxChar: 12,
      textAlign: 'center',
      showBottomBorder: group.showBottomBorder,
    },
    {
      title: 'Valor original',
      value: formatMoney(row.valorOriginal),
      maxChar: 16,
      textAlign: 'center',
      showBottomBorder: group.showBottomBorder,
    },
    {
      title: 'Desconto',
      value: formatMoney(row.desconto),
      maxChar: 14,
      textAlign: 'center',
      showBottomBorder: group.showBottomBorder,
    },
    {
      title: 'Entrada',
      value: formatMoney(row.entrada),
      maxChar: 14,
      textAlign: 'center',
      showBottomBorder: group.showBottomBorder,
    },
    {
      title: 'Parcelas',
      value:
        row.parcelas === undefined || row.parcelas === null
          ? '-'
          : String(row.parcelas),
      maxChar: 10,
      textAlign: 'center',
      showBottomBorder: group.showBottomBorder,
    },
    {
      title: 'Vencimento',
      value: formatDate(row.vencimento),
      maxChar: 12,
      textAlign: 'center',
      showBottomBorder: group.showBottomBorder,
    },
    {
      title: 'Status',
      value: row.status || '-',
      maxChar: 12,
      textAlign: 'center',
      showBottomBorder: group.showBottomBorder,
    },
    {
      title: 'Operador',
      value: row.operador || '-',
      maxChar: 16,
      textAlign: 'center',
      showBottomBorder: group.showBottomBorder,
    },
    {
      item: row,
      maxChar: 8,
      textAlign: 'center',
      searchAction: goToDetail,
      showBottomBorder: group.showBottomBorder,
    },
  ];

  const fetchPage = async (page: number, cpf = searchedCpf) => {
    const cpfOperador = String(user?.cpf || '').replace(/\D/g, '');

    if (!cpfOperador) {
      setFeedback('Não foi possível identificar o operador.');
      return;
    }

    if ((cpf && cpf.length !== 11) || listNegotiations.loading) return;

    setFeedback('');

    try {
      const response = await listNegotiations.callApi({
        cpfOperador,
        page,
        size: PAGE_SIZE,
        ...(status ? { status } : {}),
        ...(cpf ? { cpf } : {}),
      });

      if (!(response.status >= 200 && response.status < 300)) {
        setList([]);
        setTotalPages(0);
        setFeedback(response.mensagem || errorMessage);
        return;
      }

      const { items, totalPages: pages } = readNegotiationPage(response.data);
      const rows = items.map(mapItem);

      if (!rows.length) {
        setList([]);
        setTotalPages(0);
        setFeedback(emptyMessage);
        return;
      }

      setTotalPages(pages);
      setList(
        rows.map((row, index) => {
          const previous = rows[index - 1];
          const next = rows[index + 1];
          const sameAsPrevious = Boolean(previous?.cpf) && previous.cpf === row.cpf;
          const sameAsNext = Boolean(next?.cpf) && next.cpf === row.cpf;
          let rowSpan = 1;

          if (!sameAsPrevious) {
            while (rows[index + rowSpan]?.cpf === row.cpf && row.cpf) {
              rowSpan += 1;
            }
          }

          return buildRow(row, {
            rowSpan: sameAsPrevious ? undefined : rowSpan,
            hiddenIdentity: Boolean(sameAsPrevious),
            showBottomBorder: Boolean(sameAsNext),
          });
        }),
      );
    } catch (error) {
      setList([]);
      setTotalPages(0);
      setFeedback(error instanceof Error ? error.message : errorMessage);
    }
  };

  useEffect(() => {
    fetchPage(1, '');
  }, [user?.cpf]);

  const handleSearch = () => {
    setSearchedCpf(cpfDigits);
    setSearchKey((current) => current + 1);
    fetchPage(1, cpfDigits);
  };

  const handleClear = () => {
    setForm({
      cpf: { name: 'cpf', value: '', unmasked: '' },
    });
    setSearchedCpf('');
    setSearchKey((current) => current + 1);
    fetchPage(1, '');
  };

  return (
    <Container>
      <St.BoxCard>
        <ContainerFlex row space>
          <ContainerFlex padding={16}>
            <Input
              label="CPF"
              name={form.cpf.name}
              value={form.cpf.value}
              onChange={(e) => setFormState(e, form, setForm)}
              placeholder="000.000.000-00"
              maxLength={14}
              mask={MaskTypes.CPF}
            />
          </ContainerFlex>
          <ContainerFlex padding={16} row gap="12" align="flex-end">
            <Button fitContent outlined onClick={handleClear}>
              <span>Limpar pesquisa</span>
            </Button>
            <Button
              fitContent
              onClick={handleSearch}
              disabled={cpfIsPartial || listNegotiations.loading}
              loading={listNegotiations.loading}
            >
              <span>Buscar</span>
            </Button>
          </ContainerFlex>
        </ContainerFlex>
        {feedback && <St.Feedback>{feedback}</St.Feedback>}
      </St.BoxCard>

      <Loading status={listNegotiations.loading} />

      {list.length > 0 && (
        <St.BoxCard>
          <TableList customerHeader={listHeader} list={list} />
          {totalPages > 1 && (
            <ContainerFlex row center marginTop={16}>
              <Pagination
                key={searchKey}
                totalPages={totalPages}
                onCurrentPageChange={fetchPage}
              />
            </ContainerFlex>
          )}
        </St.BoxCard>
      )}
    </Container>
  );
};

export default NegotiationList;
